export interface WrappedTranslations {
  // Common
  close: string;
  share: string;

  // Intro Card
  wrappedTitle: string;
  wrappedSubtitle: string;

  // Stats Overview Card
  yourYearInNumbers: string;
  matchesPlayed: string;
  winRate: string;
  eloChange: string;
  peakElo: string;

  // ELO Journey Card
  yourEloJourney: string;
  upwardTrajectory: string;
  buildingCharacter: string;
  current: string;
  peak: string;
  lowest: string;

  // Playing Style Card
  theDominator: string;
  theDominatorDesc: string;
  iceCold: string;
  iceColdDesc: string;
  thePhoenix: string;
  thePhoenixDesc: string;
  theTactician: string;
  theTacticianDesc: string;
  cleanSheets: string;
  ofWinsWere30: string;
  totalWins: string;
  matchesWon: string;
  clutchWinRate: string;
  in5Setters: string;
  closeMatches: string;
  decidersPlayed: string;
  comebacks: string;
  winsFromBehind: string;
  mentalStrength: string;
  neverGiveUp: string;
  overallPerformance: string;
  totalGames: string;
  matchesPlayedLower: string;

  // Giant Killer Card
  giantKiller: string;
  yourBestWin: string;
  theirElo: string;
  yourElo: string;
  eloPointsGained: string;
  defeatedHigherElo: string;
  times: string;
  keepChallenging: string;

  // Clutch Master Card
  clutchMaster: string;
  thrivedUnderPressure: string;
  winRateInCloseMatches: string;
  wins: string;
  total: string;
  matchWinStreak: string;
  eloGained: string;

  // Rivals Card
  yourNemesis: string;
  opponentFacedMost: string;
  vs: string;
  losses: string;
  matchesTotal: string;

  // First Set Power Card
  firstSetPower: string;
  startingStrong: string;
  winRateWhenWinSet1: string;
  firstSetsWon: string;
  totalMatches: string;

  // Comeback Story Card
  comebackKing: string;
  neverGaveUp: string;
  winsAfterLosing1stSet: string;
  reverseSweeps: string;
  reverseSweepsDetail: string;

  // Achievements Card
  hallOfFame: string;
  yourMilestones: string;
  noBadgesYet: string;

  // Monthly Breakdown Card
  yourYearInReview: string;
  busiestMonth: string;
  withMatches: string;
  matchesPlayedLegend: string;
  matchesWonLegend: string;

  // Finale Card
  wrapped2025: string;
  whatAYear: string;
  classification: string;
  shareYourYear: string;
}

export const wrappedTranslations: Record<'en' | 'fr' | 'de' | 'it', WrappedTranslations> = {
  en: {
    // Common
    close: 'Close',
    share: 'Share',

    // Intro Card
    wrappedTitle: 'TTStats',
    wrappedSubtitle: 'Wrapped 2025',

    // Stats Overview Card
    yourYearInNumbers: 'Your Year in Numbers',
    matchesPlayed: 'Matches Played',
    winRate: 'Win Rate',
    eloChange: 'ELO Change',
    peakElo: 'Peak ELO',

    // ELO Journey Card
    yourEloJourney: 'Your ELO Journey',
    upwardTrajectory: 'Upward trajectory! 🚀',
    buildingCharacter: 'Building character! 💪',
    current: 'Current',
    peak: 'Peak',
    lowest: 'Lowest',

    // Playing Style Card
    theDominator: 'The Dominator',
    theDominatorDesc: "You don't just win, you crush it. 3-0 is your favorite scoreline.",
    iceCold: 'Ice Cold',
    iceColdDesc: 'Pressure? What pressure? You thrive in the deciding sets.',
    thePhoenix: 'The Phoenix',
    thePhoenixDesc: 'Never count you out. You turn impossible situations into victories.',
    theTactician: 'The Tactician',
    theTacticianDesc: 'Consistent, strategic, and always improving.',
    cleanSheets: 'Clean Sheets',
    ofWinsWere30: 'of your wins were 3-0',
    totalWins: 'Total Wins',
    matchesWon: 'matches won',
    clutchWinRate: 'Clutch Win Rate',
    in5Setters: 'in 5-setters',
    closeMatches: 'Close Matches',
    decidersPlayed: 'deciders played',
    comebacks: 'Comebacks',
    winsFromBehind: 'wins from behind',
    mentalStrength: 'Mental Strength',
    neverGiveUp: 'never give up',
    overallPerformance: 'overall performance',
    totalGames: 'Total Games',
    matchesPlayedLower: 'matches played',

    // Giant Killer Card
    giantKiller: 'Giant Killer',
    yourBestWin: 'Your best win of the year',
    theirElo: 'Their ELO',
    yourElo: 'Your ELO',
    eloPointsGained: 'ELO Points Gained',
    defeatedHigherElo: 'You defeated opponents with higher ELO',
    times: 'times',
    keepChallenging: 'Keep challenging stronger opponents!',

    // Clutch Master Card
    clutchMaster: 'Clutch Master',
    thrivedUnderPressure: 'You thrived under pressure',
    winRateInCloseMatches: 'Win rate in close matches',
    wins: 'Wins',
    total: 'Total',
    matchWinStreak: 'Match Win Streak',
    eloGained: 'ELO Gained',

    // Rivals Card
    yourNemesis: 'Your Nemesis',
    opponentFacedMost: 'The opponent you faced the most',
    vs: 'vs',
    losses: 'Losses',
    matchesTotal: 'matches total',

    // First Set Power Card
    firstSetPower: 'First Set Power',
    startingStrong: 'How starting strong affects your game',
    winRateWhenWinSet1: 'Win rate when you win set 1',
    firstSetsWon: 'First sets won',
    totalMatches: 'Total matches',

    // Comeback Story Card
    comebackKing: 'Comeback King',
    neverGaveUp: 'You never gave up, even when down',
    winsAfterLosing1stSet: 'Wins after losing 1st set',
    reverseSweeps: 'Reverse Sweeps',
    reverseSweepsDetail: '(0-2 to 3-2)',

    // Achievements Card
    hallOfFame: 'Hall of Fame',
    yourMilestones: 'Your milestones this year',
    noBadgesYet: 'No specific badges yet, but your journey is just beginning!',

    // Monthly Breakdown Card
    yourYearInReview: 'Your Year in Review',
    busiestMonth: 'was your busiest month with',
    withMatches: 'matches!',
    matchesPlayedLegend: 'Matches Played',
    matchesWonLegend: 'Matches Won',

    // Finale Card
    wrapped2025: 'TTStats Wrapped 2025',
    whatAYear: 'What a year',
    classification: 'Classification',
    shareYourYear: 'Share Your Year',
  },

  fr: {
    // Common
    close: 'Fermer',
    share: 'Partager',

    // Intro Card
    wrappedTitle: 'TTStats',
    wrappedSubtitle: 'Wrapped 2025',

    // Stats Overview Card
    yourYearInNumbers: 'Ton Année en Chiffres',
    matchesPlayed: 'Matchs Joués',
    winRate: 'Taux de Victoire',
    eloChange: 'Changement ELO',
    peakElo: 'ELO Maximum',

    // ELO Journey Card
    yourEloJourney: 'Ton Parcours ELO',
    upwardTrajectory: 'Trajectoire ascendante ! 🚀',
    buildingCharacter: 'Construction du caractère ! 💪',
    current: 'Actuel',
    peak: 'Maximum',
    lowest: 'Minimum',

    // Playing Style Card
    theDominator: 'Le Dominateur',
    theDominatorDesc: "Tu ne gagnes pas seulement, tu écrases. 3-0 est ton score préféré.",
    iceCold: 'Sang-Froid',
    iceColdDesc: 'Pression ? Quelle pression ? Tu excelles dans les sets décisifs.',
    thePhoenix: 'Le Phénix',
    thePhoenixDesc: 'Ne jamais te compter hors-jeu. Tu transformes l\'impossible en victoire.',
    theTactician: 'Le Tacticien',
    theTacticianDesc: 'Constant, stratégique et toujours en progression.',
    cleanSheets: 'Victoires Nettes',
    ofWinsWere30: 'de tes victoires étaient 3-0',
    totalWins: 'Victoires Totales',
    matchesWon: 'matchs gagnés',
    clutchWinRate: 'Taux Décisif',
    in5Setters: 'en 5 sets',
    closeMatches: 'Matchs Serrés',
    decidersPlayed: 'décideurs joués',
    comebacks: 'Retours',
    winsFromBehind: 'victoires alors que derrière',
    mentalStrength: 'Force Mentale',
    neverGiveUp: 'jamais abandonner',
    overallPerformance: 'performance globale',
    totalGames: 'Matchs Totaux',
    matchesPlayedLower: 'matchs joués',

    // Giant Killer Card
    giantKiller: 'Tueur de Géants',
    yourBestWin: 'Ta meilleure victoire de l\'année',
    theirElo: 'Leur ELO',
    yourElo: 'Ton ELO',
    eloPointsGained: 'Points ELO Gagnés',
    defeatedHigherElo: 'Tu as vaincu des adversaires avec un ELO supérieur',
    times: 'fois',
    keepChallenging: 'Continue à défier des adversaires plus forts !',

    // Clutch Master Card
    clutchMaster: 'Maître du Clutch',
    thrivedUnderPressure: 'Tu as excellé sous pression',
    winRateInCloseMatches: 'Taux de victoire dans les matchs serrés',
    wins: 'Victoires',
    total: 'Total',
    matchWinStreak: 'Série de Victoires',
    eloGained: 'ELO Gagné',

    // Rivals Card
    yourNemesis: 'Ton Némésis',
    opponentFacedMost: 'L\'adversaire que tu as affronté le plus',
    vs: 'vs',
    losses: 'Défaites',
    matchesTotal: 'matchs au total',

    // First Set Power Card
    firstSetPower: 'Puissance du Premier Set',
    startingStrong: 'Comment un bon départ affecte ton jeu',
    winRateWhenWinSet1: 'Taux de victoire quand tu gagnes le set 1',
    firstSetsWon: 'Premiers sets gagnés',
    totalMatches: 'Matchs totaux',

    // Comeback Story Card
    comebackKing: 'Roi du Comeback',
    neverGaveUp: 'Tu n\'as jamais abandonné, même en difficulté',
    winsAfterLosing1stSet: 'Victoires après avoir perdu le 1er set',
    reverseSweeps: 'Remontées Complètes',
    reverseSweepsDetail: '(0-2 à 3-2)',

    // Achievements Card
    hallOfFame: 'Panthéon',
    yourMilestones: 'Tes jalons cette année',
    noBadgesYet: 'Pas encore de badges spécifiques, mais ton parcours ne fait que commencer !',

    // Monthly Breakdown Card
    yourYearInReview: 'Ton Année en Revue',
    busiestMonth: 'était ton mois le plus chargé avec',
    withMatches: 'matchs !',
    matchesPlayedLegend: 'Matchs Joués',
    matchesWonLegend: 'Matchs Gagnés',

    // Finale Card
    wrapped2025: 'TTStats Wrapped 2025',
    whatAYear: 'Quelle année',
    classification: 'Classification',
    shareYourYear: 'Partage Ton Année',
  },

  de: {
    // Common
    close: 'Schließen',
    share: 'Teilen',

    // Intro Card
    wrappedTitle: 'TTStats',
    wrappedSubtitle: 'Wrapped 2025',

    // Stats Overview Card
    yourYearInNumbers: 'Dein Jahr in Zahlen',
    matchesPlayed: 'Gespielte Spiele',
    winRate: 'Siegquote',
    eloChange: 'ELO-Änderung',
    peakElo: 'Höchste ELO',

    // ELO Journey Card
    yourEloJourney: 'Deine ELO-Reise',
    upwardTrajectory: 'Aufwärtstrend! 🚀',
    buildingCharacter: 'Charakterbildung! 💪',
    current: 'Aktuell',
    peak: 'Höchste',
    lowest: 'Niedrigste',

    // Playing Style Card
    theDominator: 'Der Dominator',
    theDominatorDesc: "Du gewinnst nicht nur, du zerquetschst es. 3-0 ist dein Lieblingsergebnis.",
    iceCold: 'Eiskalt',
    iceColdDesc: 'Druck? Welcher Druck? Du glänzt in den Entscheidungssätzen.',
    thePhoenix: 'Der Phönix',
    thePhoenixDesc: 'Schreib dich nie ab. Du verwandelst unmögliche Situationen in Siege.',
    theTactician: 'Der Taktiker',
    theTacticianDesc: 'Beständig, strategisch und immer besser werdend.',
    cleanSheets: 'Zu-Null-Siege',
    ofWinsWere30: 'deiner Siege waren 3-0',
    totalWins: 'Gesamtsiege',
    matchesWon: 'gewonnene Spiele',
    clutchWinRate: 'Clutch-Siegquote',
    in5Setters: 'in 5-Satz-Spielen',
    closeMatches: 'Knappe Spiele',
    decidersPlayed: 'Entscheidungssätze gespielt',
    comebacks: 'Comebacks',
    winsFromBehind: 'Siege nach Rückstand',
    mentalStrength: 'Mentale Stärke',
    neverGiveUp: 'niemals aufgeben',
    overallPerformance: 'Gesamtleistung',
    totalGames: 'Gesamtspiele',
    matchesPlayedLower: 'gespielte Spiele',

    // Giant Killer Card
    giantKiller: 'Riesentöter',
    yourBestWin: 'Dein bester Sieg des Jahres',
    theirElo: 'Sein/Ihr ELO',
    yourElo: 'Dein ELO',
    eloPointsGained: 'ELO-Punkte Gewonnen',
    defeatedHigherElo: 'Du hast Gegner mit höherer ELO besiegt',
    times: 'mal',
    keepChallenging: 'Fordere weiterhin stärkere Gegner heraus!',

    // Clutch Master Card
    clutchMaster: 'Clutch-Meister',
    thrivedUnderPressure: 'Du hast unter Druck brilliert',
    winRateInCloseMatches: 'Siegquote in knappen Spielen',
    wins: 'Siege',
    total: 'Gesamt',
    matchWinStreak: 'Siegesserie',
    eloGained: 'ELO Gewonnen',

    // Rivals Card
    yourNemesis: 'Dein Erzrivale',
    opponentFacedMost: 'Der Gegner, dem du am häufigsten begegnet bist',
    vs: 'vs',
    losses: 'Niederlagen',
    matchesTotal: 'Spiele insgesamt',

    // First Set Power Card
    firstSetPower: 'Erster-Satz-Power',
    startingStrong: 'Wie ein starker Start dein Spiel beeinflusst',
    winRateWhenWinSet1: 'Siegquote, wenn du Satz 1 gewinnst',
    firstSetsWon: 'Erste Sätze gewonnen',
    totalMatches: 'Gesamtspiele',

    // Comeback Story Card
    comebackKing: 'Comeback-König',
    neverGaveUp: 'Du hast nie aufgegeben, selbst im Rückstand',
    winsAfterLosing1stSet: 'Siege nach verlorenen 1. Satz',
    reverseSweeps: 'Komplette Aufholjagden',
    reverseSweepsDetail: '(0-2 zu 3-2)',

    // Achievements Card
    hallOfFame: 'Ruhmeshalle',
    yourMilestones: 'Deine Meilensteine dieses Jahr',
    noBadgesYet: 'Noch keine spezifischen Abzeichen, aber deine Reise beginnt gerade erst!',

    // Monthly Breakdown Card
    yourYearInReview: 'Dein Jahr im Rückblick',
    busiestMonth: 'war dein geschäftigster Monat mit',
    withMatches: 'Spielen!',
    matchesPlayedLegend: 'Gespielte Spiele',
    matchesWonLegend: 'Gewonnene Spiele',

    // Finale Card
    wrapped2025: 'TTStats Wrapped 2025',
    whatAYear: 'Was für ein Jahr',
    classification: 'Klassifizierung',
    shareYourYear: 'Teile Dein Jahr',
  },

  it: {
    // Common
    close: 'Chiudi',
    share: 'Condividi',

    // Intro Card
    wrappedTitle: 'TTStats',
    wrappedSubtitle: 'Wrapped 2025',

    // Stats Overview Card
    yourYearInNumbers: 'Il Tuo Anno in Numeri',
    matchesPlayed: 'Partite Giocate',
    winRate: 'Tasso di Vittoria',
    eloChange: 'Cambio ELO',
    peakElo: 'ELO Massimo',

    // ELO Journey Card
    yourEloJourney: 'Il Tuo Percorso ELO',
    upwardTrajectory: 'Traiettoria ascendente! 🚀',
    buildingCharacter: 'Costruzione del carattere! 💪',
    current: 'Attuale',
    peak: 'Massimo',
    lowest: 'Minimo',

    // Playing Style Card
    theDominator: 'Il Dominatore',
    theDominatorDesc: "Non vinci solo, schiacci. 3-0 è il tuo punteggio preferito.",
    iceCold: 'Sangue Freddo',
    iceColdDesc: 'Pressione? Quale pressione? Prosperi nei set decisivi.',
    thePhoenix: 'La Fenice',
    thePhoenixDesc: 'Mai contarti fuori. Trasformi situazioni impossibili in vittorie.',
    theTactician: 'Il Tattico',
    theTacticianDesc: 'Coerente, strategico e sempre in miglioramento.',
    cleanSheets: 'Vittorie Nette',
    ofWinsWere30: 'delle tue vittorie erano 3-0',
    totalWins: 'Vittorie Totali',
    matchesWon: 'partite vinte',
    clutchWinRate: 'Tasso Clutch',
    in5Setters: 'in 5 set',
    closeMatches: 'Partite Serrate',
    decidersPlayed: 'decisivi giocati',
    comebacks: 'Rimonte',
    winsFromBehind: 'vittorie da dietro',
    mentalStrength: 'Forza Mentale',
    neverGiveUp: 'mai arrendersi',
    overallPerformance: 'prestazione complessiva',
    totalGames: 'Partite Totali',
    matchesPlayedLower: 'partite giocate',

    // Giant Killer Card
    giantKiller: 'Ammazza Giganti',
    yourBestWin: 'La tua migliore vittoria dell\'anno',
    theirElo: 'Il Loro ELO',
    yourElo: 'Il Tuo ELO',
    eloPointsGained: 'Punti ELO Guadagnati',
    defeatedHigherElo: 'Hai sconfitto avversari con ELO superiore',
    times: 'volte',
    keepChallenging: 'Continua a sfidare avversari più forti!',

    // Clutch Master Card
    clutchMaster: 'Maestro del Clutch',
    thrivedUnderPressure: 'Hai prosperato sotto pressione',
    winRateInCloseMatches: 'Tasso di vittoria in partite serrate',
    wins: 'Vittorie',
    total: 'Totale',
    matchWinStreak: 'Serie di Vittorie',
    eloGained: 'ELO Guadagnato',

    // Rivals Card
    yourNemesis: 'La Tua Nemesi',
    opponentFacedMost: 'L\'avversario che hai affrontato di più',
    vs: 'vs',
    losses: 'Sconfitte',
    matchesTotal: 'partite totali',

    // First Set Power Card
    firstSetPower: 'Potenza del Primo Set',
    startingStrong: 'Come un inizio forte influenza il tuo gioco',
    winRateWhenWinSet1: 'Tasso di vittoria quando vinci il set 1',
    firstSetsWon: 'Primi set vinti',
    totalMatches: 'Partite totali',

    // Comeback Story Card
    comebackKing: 'Re del Comeback',
    neverGaveUp: 'Non ti sei mai arreso, anche in svantaggio',
    winsAfterLosing1stSet: 'Vittorie dopo aver perso il 1° set',
    reverseSweeps: 'Rimonte Complete',
    reverseSweepsDetail: '(0-2 a 3-2)',

    // Achievements Card
    hallOfFame: 'Sala della Fama',
    yourMilestones: 'I tuoi traguardi quest\'anno',
    noBadgesYet: 'Nessun badge specifico ancora, ma il tuo viaggio è appena iniziato!',

    // Monthly Breakdown Card
    yourYearInReview: 'Il Tuo Anno in Rassegna',
    busiestMonth: 'è stato il tuo mese più intenso con',
    withMatches: 'partite!',
    matchesPlayedLegend: 'Partite Giocate',
    matchesWonLegend: 'Partite Vinte',

    // Finale Card
    wrapped2025: 'TTStats Wrapped 2025',
    whatAYear: 'Che anno',
    classification: 'Classificazione',
    shareYourYear: 'Condividi Il Tuo Anno',
  },
};
